import React, { useEffect, useState } from 'react';
import {
  Card,
  Avatar,
  Typography,
  Descriptions,
  Divider,
  Spin,
  Button,
  message,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

const { Title, Text } = Typography;

interface UserData {
  id: string;
  email: string;
  name: string;
  role: string;
  phone: string;
}

const MyAccount: React.FC = () => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation() as { t: (key: string) => string };
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    // Load user data from localStorage
    const loadUserData = () => {
      try {
        setLoading(true);
        const storedData = localStorage.getItem('fruitshop_user');

        if (storedData) {
          const parsedData = JSON.parse(storedData);
          setUserData(parsedData);
        } else {
          // Redirect to login if no user data found
          message.warning(t('auth.pleaseLogin'));
          navigate('/auth/login');
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        message.error(t('errors.somethingWentWrong'));
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [navigate, t]);

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('fruitshop_user');
      message.success(t('auth.logoutSuccess'));
      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
      message.error(t('errors.somethingWentWrong'));
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <Spin size='large' />
      </div>
    );
  }

  if (!userData) {
    return null; // Will redirect in useEffect
  }

  // Get first letter of name for avatar
  const avatarText = userData.name
    ? userData.name.charAt(0).toUpperCase()
    : '?';

  return (
    <div className='container mx-auto px-4 py-8'>
      <Title level={2}>{t('account.myProfile')}</Title>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        {/* Profile Overview */}
        <Card className='col-span-1 shadow-md'>
          <div className='flex flex-col items-center text-center'>
            <Avatar
              size={80}
              style={{ backgroundColor: '#1890ff', marginBottom: '16px' }}
            >
              {avatarText}
            </Avatar>
            <Title level={3}>{userData.name}</Title>
            <Text type='secondary'>{userData.role}</Text>
            <Divider />
            <div className='w-full flex flex-col gap-2'>
              <div className='flex items-center'>
                <MailOutlined className='mr-2' />
                <Text>{userData.email}</Text>
              </div>
              <div className='flex items-center'>
                <PhoneOutlined className='mr-2' />
                <Text>{userData.phone || t('account.noPhoneProvided')}</Text>
              </div>
            </div>
            <Divider />
            <Button
              type='primary'
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              className='w-full'
            >
              {t('common.logout')}
            </Button>
          </div>
        </Card>

        {/* Account Details */}
        <Card className='col-span-1 md:col-span-2 shadow-md'>
          <Title level={4}>{t('account.accountDetails')}</Title>
          <Descriptions layout='vertical' bordered column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label={t('account.userId')}>
              {userData.id}
            </Descriptions.Item>
            <Descriptions.Item label={t('auth.name')}>
              {userData.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('auth.email')}>
              {userData.email}
            </Descriptions.Item>
            <Descriptions.Item label={t('auth.phone')}>
              {userData.phone || t('account.noPhoneProvided')}
            </Descriptions.Item>
            <Descriptions.Item label={t('account.accountType')}>
              {userData.role === 'user' ? t('account.customer') : userData.role}
            </Descriptions.Item>
          </Descriptions>

          <div className='mt-6'>
            <Button type='primary' onClick={() => navigate('/order-history')}>
              {t('cart.myOrders')}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MyAccount;
